/**
 * Estoque da Oficina — Iter65
 * Lista de equipamentos com status='na_oficina' + gestão de sub_status.
 * Rota: /app/oficina/estoque
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Wrench, CheckCircle2, Clock, Loader2, Search, PackageCheck,
} from "lucide-react";
import { toast } from "sonner";

const SUB_STATUS_CFG = {
  disponivel_para_troca: {
    label: "Disponível p/ uso",
    cls: "bg-emerald-100 text-emerald-800 border-emerald-300",
    icon: CheckCircle2,
    filterLabel: "Disponíveis",
  },
  em_manutencao: {
    label: "Em manutenção",
    cls: "bg-amber-100 text-amber-800 border-amber-300",
    icon: Wrench,
    filterLabel: "Em manutenção",
  },
  aguardando_pecas: {
    label: "Aguard. peças",
    cls: "bg-red-100 text-red-700 border-red-300",
    icon: Clock,
    filterLabel: "Aguard. peças",
  },
};

const AÇÕES = [
  { key: "disponivel_para_troca", label: "Disponível", icon: CheckCircle2, cls: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "em_manutencao", label: "Manutenção", icon: Wrench, cls: "bg-amber-600 hover:bg-amber-700" },
  { key: "aguardando_pecas", label: "Aguard. peças", icon: Clock, cls: "bg-red-600 hover:bg-red-700" },
];

export default function OficinaEstoque() {
  const { user } = useAuth();
  const podeEditar = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [q, setQ] = useState("");
  const [filterSub, setFilterSub] = useState(""); // "" = todos

  const loadBreakdown = useCallback(async () => {
    try {
      const { data } = await api.get("/inventario/oficina/breakdown");
      setBreakdown(data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filterSub) params.sub_status = filterSub;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/inventario/oficina/equipamentos", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erro ao carregar estoque da oficina");
    } finally {
      setLoading(false);
    }
  }, [filterSub, q]);

  useEffect(() => { loadBreakdown(); }, [loadBreakdown]);
  useEffect(() => {
    const t = setTimeout(() => loadList(), 250);
    return () => clearTimeout(t);
  }, [loadList]);

  const mudarSubStatus = async (eq, novo) => {
    if (eq.sub_status === novo) return;
    setSavingId(eq.id);
    try {
      await api.patch(`/inventario/equipamentos/${eq.id}/sub-status`, {
        sub_status: novo,
      });
      toast.success(`${eq.serial} → ${SUB_STATUS_CFG[novo].label}`);
      // Se há filtro ativo e o novo sub_status não bate, recarrega para tirar a linha da tela
      if (filterSub && novo !== filterSub) {
        loadList();
      } else {
        setItems((prev) =>
          prev.map((x) => (x.id === eq.id ? { ...x, sub_status: novo } : x))
        );
      }
      loadBreakdown();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar sub-status");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="oficina-estoque-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PackageCheck className="text-orange-600" />
            Estoque da Oficina
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Gerencie o sub-status dos equipamentos em oficina. Somente <strong>Disponível p/ uso</strong> pode ser vinculado a cliente ou usado em troca.
          </p>
        </div>
      </div>

      {/* Breakdown cards */}
      {breakdown && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="oficina-breakdown">
          <Card
            className={`p-3 cursor-pointer transition ${filterSub === "" ? "ring-2 ring-slate-800" : "hover:bg-slate-50"}`}
            onClick={() => setFilterSub("")}
            data-testid="filter-todos"
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total oficina</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{breakdown.total_na_oficina}</div>
          </Card>
          {Object.entries(SUB_STATUS_CFG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const active = filterSub === key;
            return (
              <Card
                key={key}
                className={`p-3 cursor-pointer transition ${active ? "ring-2 ring-orange-500" : "hover:bg-slate-50"}`}
                onClick={() => setFilterSub(key)}
                data-testid={`filter-${key}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <Icon className="w-3 h-3" />
                  {cfg.filterLabel}
                </div>
                <div className="text-2xl font-bold mt-1" style={{ color: cfg.cls.match(/text-(\w+)-\d+/)?.[0].replace("text-", "").replace("bg-", "") ? undefined : undefined }}>
                  <span className={cfg.cls.split(" ").find((c) => c.startsWith("text-"))}>{breakdown[key]}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Busca */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
        <Input
          className="pl-8"
          placeholder="Buscar serial, placa ou modelo..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="oficina-busca-input"
        />
      </div>

      {/* Tabela */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            Nenhum equipamento encontrado{filterSub ? ` com sub-status "${SUB_STATUS_CFG[filterSub]?.label}"` : ""}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serial</TableHead>
                  <TableHead>Placa</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Sub-status</TableHead>
                  {podeEditar && <TableHead className="text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((eq) => {
                  const sub = eq.sub_status || "em_manutencao";
                  const cfg = SUB_STATUS_CFG[sub] || SUB_STATUS_CFG.em_manutencao;
                  const Icon = cfg.icon;
                  const salvando = savingId === eq.id;
                  return (
                    <TableRow key={eq.id} data-testid={`oficina-row-${eq.id}`}>
                      <TableCell className="font-mono text-xs">{eq.serial}</TableCell>
                      <TableCell className="font-mono text-xs">{eq.placa || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-600">{eq.modelo || "—"}</TableCell>
                      <TableCell>
                        <Badge className={`${cfg.cls} border text-[10px] inline-flex items-center gap-1`}>
                          <Icon className="w-3 h-3" />
                          {cfg.label}
                        </Badge>
                      </TableCell>
                      {podeEditar && (
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            {AÇÕES.map((a) => {
                              const ActIcon = a.icon;
                              const ativo = eq.sub_status === a.key;
                              return (
                                <Button
                                  key={a.key}
                                  size="sm"
                                  disabled={salvando || ativo}
                                  onClick={() => mudarSubStatus(eq, a.key)}
                                  className={`h-7 text-[10px] px-2 ${ativo ? "bg-slate-300 text-slate-600 cursor-not-allowed" : `${a.cls} text-white`}`}
                                  data-testid={`btn-set-${a.key}-${eq.id}`}
                                  title={ativo ? "Já está neste sub-status" : a.label}
                                >
                                  {salvando && !ativo ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <ActIcon className="w-3 h-3 mr-1" />
                                  )}
                                  {a.label}
                                </Button>
                              );
                            })}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
